import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BASE_URL, ENDPOINTS } from '../../apiConfig';
import {
  Trophy, Sparkles, Gem, Flower2, Flame, Sun, ArrowLeft,
  Disc, Waves, Monitor, Smartphone, ShoppingBag
} from 'lucide-react';
import gsap from 'gsap';
import './Categories.css';

const getCategoryIcon = (name) => {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('cricket') || lowerName.includes('bat')) return <Trophy size={30} strokeWidth={1} />;
  if (lowerName.includes('ball')) return <Disc size={30} strokeWidth={1} />;
  if (lowerName.includes('electronic')) return <Smartphone size={30} strokeWidth={1} />;
  if (lowerName.includes('computer') || lowerName.includes('laptop')) return <Monitor size={30} strokeWidth={1} />;
  if (lowerName.includes('perfume')) return <Sparkles size={30} strokeWidth={1} />;
  if (lowerName.includes('floral') || lowerName.includes('flower') || lowerName.includes('rose')) return <Flower2 size={30} strokeWidth={1} />;
  if (lowerName.includes('oud') || lowerName.includes('wood') || lowerName.includes('musk')) return <Flame size={30} strokeWidth={1} />;
  if (lowerName.includes('fresh') || lowerName.includes('aqua') || lowerName.includes('ocean')) return <Waves size={30} strokeWidth={1} />;
  if (lowerName.includes('summer') || lowerName.includes('citrus')) return <Sun size={30} strokeWidth={1} />;
  if (lowerName.length <= 3 || lowerName.includes('pkr')) return <ShoppingBag size={30} strokeWidth={1} />;
  return <Gem size={30} strokeWidth={1} />;
};

export default function Categories() {
  const navigate = useNavigate();
  const [allCategories, setAllCategories] = useState([]);
  const [displayCategories, setDisplayCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentParent, setCurrentParent] = useState(null);
  const sectionRef = useRef(null);
  const sliderRef = useRef(null);

  // Seamless GSAP Marquee Animation
  useEffect(() => {
    if (!sliderRef.current || displayCategories.length === 0) return;
    
    const card = sliderRef.current.querySelector('.category-card--slide');
    if (!card) return;

    const cardWidth = card.offsetWidth;
    const gap = window.innerWidth <= 768 ? 12 : 30;
    const N = displayCategories.length;
    
    // total width of one original set of categories
    const totalWidth = N * (cardWidth + gap);

    // Continuous linear animation
    const tween = gsap.to(sliderRef.current, {
      x: -totalWidth,
      duration: N * 2, // speed: 2 seconds per card
      ease: "none",
      repeat: -1
    });

    const slider = sliderRef.current;
    const pause = () => tween.pause();
    const play = () => tween.play();

    slider.addEventListener('mouseenter', pause);
    slider.addEventListener('mouseleave', play);

    return () => {
      tween.kill();
      slider.removeEventListener('mouseenter', pause);
      slider.removeEventListener('mouseleave', play);
    };
  }, [displayCategories]);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const [catRes, prodRes] = await Promise.all([
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_CATEGORIES}`, { headers }),
        fetch(`${BASE_URL}${ENDPOINTS.GET_ALL_PRODUCTS}`, { headers })
      ]);

      const result = await catRes.json();
      const prodResult = await prodRes.json();

      const catData = Array.isArray(result.data) ? result.data : (Array.isArray(result) ? result : []);
      const prodData = Array.isArray(prodResult) ? prodResult : (prodResult.data || []);

      setAllCategories(catData);
      setDisplayCategories(catData.filter(cat => !cat.parentCategory));
      setProducts(prodData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const getSubCategoryCount = (parentId) =>
    allCategories.filter(cat => {
      const pId = typeof cat.parentCategory === 'object' ? cat.parentCategory?._id : cat.parentCategory;
      return pId === parentId;
    }).length;

  const handleCategoryClick = (category) => {
    // 1. Check if there is any product directly belonging to this category first
    const targetProd = products.find(p => {
      const pCatId = typeof p.categoryId === 'object' ? p.categoryId?._id : p.categoryId;
      return pCatId === category._id;
    });

    if (targetProd) {
      // Direct navigation to this product's separate details page!
      navigate(`/product/${targetProd._id || targetProd.id}`);
    } else {
      // 2. If no direct product, check if there are subcategories to drill down into
      const children = allCategories.filter(cat => {
        const pId = typeof cat.parentCategory === 'object' ? cat.parentCategory?._id : cat.parentCategory;
        return pId === category._id;
      });

      if (children.length > 0) {
        gsap.to(".category-card", {
          opacity: 0, y: 20, stagger: 0.05, duration: 0.3,
          onComplete: () => {
            setDisplayCategories(children);
            setCurrentParent(category);
            window.scrollTo({ top: sectionRef.current.offsetTop - 100, behavior: 'smooth' });
          }
        });
      } else {
        // 3. Fallback: if no direct product and no subcategories, navigate to shop filtered by category
        navigate(`/shop?category=${category._id}`);
      }
    }
  };

  const handleBack = () => {
    gsap.to(".category-card", {
      opacity: 0, y: 20, stagger: 0.05, duration: 0.3,
      onComplete: () => {
        setDisplayCategories(allCategories.filter(cat => !cat.parentCategory));
        setCurrentParent(null);
      }
    });
  };

  if (loading) return <div className="luxury-loader"><div className="loader-gold-circle"></div></div>;

  return (
    <section className="categories-section" ref={sectionRef}>
      <div className="categories-container">
        <div className="categories-header">
          {currentParent && (
            <div className="back-nav" onClick={handleBack}>
              <ArrowLeft size={16} /> <span>BACK TO ALL</span>
            </div>
          )}
          <h2 className="title">
            {currentParent ? currentParent.name : 'SHOP BY CATEGORY '}
            
          </h2>
          <div className="header-line"></div>
        </div>

        {/* Clean Auto-Carousel without Arrow Buttons */}
        <div className="categories-carousel-outer">
          <div className="categories-slider-mask">
            <div className="categories-slider-track" ref={sliderRef}>
              {[...displayCategories, ...displayCategories, ...displayCategories].map((category, idx) => {
                const childCount = getSubCategoryCount(category._id);
                return (
                  <div key={`${category._id}-${idx}`} className="category-card category-card--slide" onClick={() => handleCategoryClick(category)}>
                    <div className="cat-icon-circle">
                      {getCategoryIcon(category.name)}
                    </div>
                    <div className="category-info-outside">
                      <h3 className="cat-name-mini">{category.name}</h3>
                      {childCount > 0 ? (
                        <p className="sub-count-text">{childCount} SUB-CATEGORIES</p>
                      ) : (
                        <div className="cat-explore-mini">EXPLORE <span className="arrow">→</span></div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
